// =============================================================================
// src/features/explore/explore-g26-locais-layer.ts — Camada "Locais de votação
//   2026" do Explorar: um ponto por local (escola/predio) com o resultado OFICIAL
//   do cargo ativo, somado das secoes (TSE votacao_secao, 1o turno 04/10/2026).
//
// - Baixa indice + votos do cargo so ao ligar (G26 store, memoizado por cargo).
// - Ponto na cor do partido do mais votado do local; raio ~ sqrt(votos).
// - Canvas renderer: ~1.500 pontos sem 1.500 nos SVG.
// - Locais sem coordenada (sem par no cadastro TSE 2024) ficam fora do mapa.
//
// Leaflet 1.9.4 (L.circleMarker/canvas/layerGroup/popup), mesma API ja usada
//   no explore-vote-layer — contrato travado pelo @types/leaflet + tsc.
// =============================================================================

import L from 'leaflet';
import type { LayerGroup, Map as LeafletMap, Renderer } from 'leaflet';
import type { G26CargoCode, G26Dataset, G26Local, G26LocalVotos } from './explore-g26-types';
import type { ExploreG26Store } from './explore-g26-store';
import { g26PartyColor } from './explore-g26-constants';
import { buildG26LocalPopupHtml } from './explore-g26-card';
import { EXPLORE_TALL_POPUP_OPTIONS } from './explore-tse-zone-layer';

/** Pane dos locais: acima das zonas TSE (450), abaixo dos marcadores (600). */
export const G26_LOCAIS_PANE = 'expG26LocaisPane';
const G26_LOCAIS_PANE_Z_INDEX = '470';
/** Raio (px) minimo/maximo do ponto. */
export const G26_LOCAL_MIN_RADIUS = 3;
export const G26_LOCAL_MAX_RADIUS = 13;
const NEUTRAL_LOCAL_COLOR = '#8A8F98';

/** Raio do ponto por votos (sqrt, area ~ votos) entre MIN e MAX. */
export function g26LocalRadius(votos: number, maxVotos: number): number {
  const share = maxVotos > 0 ? Math.sqrt(Math.min(1, votos / maxVotos)) : 0;
  return G26_LOCAL_MIN_RADIUS + (G26_LOCAL_MAX_RADIUS - G26_LOCAL_MIN_RADIUS) * share;
}

/** Cor do partido do mais votado no local (neutra sem voto nominal). */
export function g26LocalColor(ds: G26Dataset, votos: G26LocalVotos | null): string {
  const first = votos?.candidatos[0];
  const cand = first ? ds.candidatos[first[0]] : undefined;
  return cand ? g26PartyColor(cand.partido) : NEUTRAL_LOCAL_COLOR;
}

/** Camada dos locais de votacao 2026 (liga/desliga e troca de cargo). */
export class ExploreG26LocaisLayer {
  private readonly group: LayerGroup = L.layerGroup();
  private renderer: Renderer | null = null;
  private visible = false;
  private cargo: G26CargoCode;
  private drawSeq = 0;

  constructor(private readonly map: LeafletMap, private readonly store: ExploreG26Store, cargo: G26CargoCode) {
    this.cargo = cargo;
  }

  /**
   * Liga/desliga. Resolve `false` se algum asset falhar (a UI reverte o switch).
   *
   * @example await locais.setVisible(true) // true quando desenhou
   */
  async setVisible(on: boolean): Promise<boolean> {
    this.visible = on;
    if (!on) { this.map.removeLayer(this.group); return true; }
    this.group.addTo(this.map);
    return this.redraw();
  }

  /** Troca o cargo; redesenha se estiver visivel. */
  async setCargo(cargo: G26CargoCode): Promise<boolean> {
    this.cargo = cargo;
    return this.visible ? this.redraw() : true;
  }

  /** Busca os assets do cargo atual e repinta (descarta resposta de cargo antigo). */
  private async redraw(): Promise<boolean> {
    const seq = ++this.drawSeq;
    try {
      const [ds, index, votos] = await Promise.all([this.store.loadDataset(), this.store.loadLocais(), this.store.loadLocaisVotos(this.cargo)]);
      if (seq === this.drawSeq && this.visible) this.draw(ds, index.locais, votos.votos);
      return true;
    } catch (error) {
      console.warn(JSON.stringify({ event: 'explore.g26-locais.load-failed', cargo: this.cargo, message: String(error) }));
      return false;
    }
  }

  /** Limpa e desenha um ponto por local com coordenada. */
  private draw(ds: G26Dataset, locais: readonly G26Local[], votos: readonly (G26LocalVotos | null)[]): void {
    this.group.clearLayers();
    const renderer = this.ensureRenderer();
    const maxVotos = votos.reduce((max, v) => Math.max(max, v?.votos ?? 0), 0);
    locais.forEach((local, i) => {
      if (local.lat === null || local.lng === null) return;
      this.group.addLayer(this.localMarker(ds, local, votos[i] ?? null, maxVotos, renderer));
    });
  }

  /** Ponto clicavel de um local (popup com o resultado do cargo). */
  private localMarker(ds: G26Dataset, local: G26Local, votos: G26LocalVotos | null, maxVotos: number, renderer: Renderer): L.CircleMarker {
    const marker = L.circleMarker([local.lat as number, local.lng as number], {
      renderer, pane: G26_LOCAIS_PANE, radius: g26LocalRadius(votos?.votos ?? 0, maxVotos),
      fillColor: g26LocalColor(ds, votos), fillOpacity: 0.85, color: '#FFFFFF', weight: 1,
    });
    const cargo = this.cargo;
    marker.bindPopup(() => buildG26LocalPopupHtml(ds, local, votos, cargo), { ...EXPLORE_TALL_POPUP_OPTIONS });
    return marker;
  }

  /** Pane + canvas renderer criados uma vez. */
  private ensureRenderer(): Renderer {
    if (!this.map.getPane(G26_LOCAIS_PANE)) this.map.createPane(G26_LOCAIS_PANE).style.zIndex = G26_LOCAIS_PANE_Z_INDEX;
    this.renderer ??= L.canvas({ pane: G26_LOCAIS_PANE, padding: 0.5 });
    return this.renderer;
  }
}
